import React, { FC, useContext, useEffect, useState } from 'react';

import { useMutation, useQuery } from '@apollo/client';
import {
  AlertIcon,
  Button,
  H2,
  Link,
  Modal,
  Preloader,
} from '@smartshell/ui-kit';
import cn from 'classnames';

import { LINKS } from 'links';
import { LocalizationContext } from 'localization';
import notificationService from 'services/notificationService';
import {
  CONNECT_PAYMENT_SERVICES,
  EConnectPaymentService,
  ESettingsAliases,
  GET_BENEFICIARIES,
  GET_CURRENT_BENEFICIARY,
  IConnectPaymentServiceRequest,
  IGetBeneficiariesResponse,
  IGetCurrentBeneficiaryResponse,
  SettingsContext,
} from 'settings';

import { IBaseModalProps } from '../interfaces';
import { BeneficiaryCard, CreateBeneficiary } from './components';
import { EBeneficiaryStatus } from './interfaces';
import s from './TinkoffModal.module.scss';

export const TinkoffModal: FC<IBaseModalProps> = ({ onClose }) => {
  const { t, lang } = useContext(LocalizationContext);

  const {
    state: { settings, isLoading },
    actions: { setSetting },
  } = useContext(SettingsContext);

  const { data: getBeneficiaries, loading } =
    useQuery<IGetBeneficiariesResponse>(GET_BENEFICIARIES, {
      fetchPolicy: 'no-cache',
    });

  const { data, stopPolling, startPolling } =
    useQuery<IGetCurrentBeneficiaryResponse>(GET_CURRENT_BENEFICIARY, {
      fetchPolicy: 'no-cache',
    });

  useEffect(() => {
    if (data?.getCurrentBeneficiary?.status === EBeneficiaryStatus.PENDING) {
      startPolling(5000);
    } else {
      stopPolling();
    }
  }, [data]);

  const [connectPaymentService, { loading: connectLoading }] = useMutation<
    boolean,
    IConnectPaymentServiceRequest
  >(CONNECT_PAYMENT_SERVICES, {
    refetchQueries: [GET_CURRENT_BENEFICIARY],
    awaitRefetchQueries: true,
  });

  const [isCreate, setIsCreate] = useState(false);
  const [checkedBeneficiary, setCheckedBeneficiary] = useState<string>('');

  useEffect(() => {
    if (
      getBeneficiaries?.getBeneficiaries &&
      getBeneficiaries?.getBeneficiaries.length > 0
    ) {
      return setIsCreate(false);
    }

    setIsCreate(true);
  }, [getBeneficiaries]);

  return (
    <Modal
      onClose={onClose}
      className={cn(s.modal, isCreate && !loading && s.modal_big)}
      disableOutsideClose
    >
      <Preloader loading={loading} className={s.preloader}>
        {data?.getCurrentBeneficiary ? (
          <div className={s.result_block}>
            <H2 className={s.result_title}>Онлайн-СБП</H2>
            <BeneficiaryCard beneficiary={data.getCurrentBeneficiary} />
            {data.getCurrentBeneficiary.status ===
              EBeneficiaryStatus.APPROVED && (
              <div className={s.info}>
                <AlertIcon color="warning" />
                <span>
                  Если вы хотите изменить бенефициара или реквизиты для вывода -
                  обратитесь в техническую поддержку и пришлите данные, на
                  которые вы хотите поменять.{' '}
                  <Link
                    className={s.link}
                    title={'Подробнее'}
                    link={LINKS.sbp_tinkoff[lang]}
                  />
                </span>
              </div>
            )}
            <Button
              variant="secondary"
              isLoading={isLoading}
              onClick={() => {
                setSetting({
                  alias: ESettingsAliases.TINKOFF_SBP_ENABLED,
                  value: '0',
                }).then(onClose);
              }}
              className={s.result_button}
              fullWidth
              disabled={!settings.tinkoffSbpEnabled}
            >
              {t('integration.offline')}
            </Button>
          </div>
        ) : (
          <>
            {isCreate ? (
              <H2 className={s.title}>Подключение СБП</H2>
            ) : (
              <>
                <H2 className={s.title}>Онлайн-СБП</H2>
                <div className={s.info}>
                  <AlertIcon color="warning" />
                  <span>
                    Для корректной работы добавьте бенефициара, на чей счет
                    будут перечисляться средства.
                  </span>
                </div>
              </>
            )}

            <div className={s.wrapper}>
              {isCreate && <CreateBeneficiary onClose={onClose} />}
              {!isCreate && getBeneficiaries?.getBeneficiaries && (
                <div className={s.beneficiaries}>
                  {getBeneficiaries.getBeneficiaries.map(
                    (beneficiary) =>
                      beneficiary.status === EBeneficiaryStatus.APPROVED && (
                        <BeneficiaryCard
                          key={beneficiary.id}
                          beneficiary={beneficiary}
                          onClick={() => {
                            setCheckedBeneficiary(beneficiary.id);
                          }}
                          checkedBeneficiary={checkedBeneficiary}
                        />
                      )
                  )}
                  {!isCreate && (
                    <Button
                      iconName="CirclePlusIcon"
                      className={s.create_button}
                      onClick={() => setIsCreate(true)}
                      disabled={connectLoading}
                      variant="secondary"
                    >
                      Добавить бенефициара
                    </Button>
                  )}
                </div>
              )}
            </div>
            {!isCreate && (
              <div className={s.buttons_block}>
                <Button
                  fullWidth
                  onClick={() => {
                    connectPaymentService({
                      variables: {
                        input: {
                          existing_beneficiary: checkedBeneficiary,
                        },
                        service: EConnectPaymentService.TINKOFF,
                      },
                    })
                      .then(() =>
                        notificationService.info({
                          t,
                          title: t('setting.updated'),
                          type: 'success',
                          dismiss: 3,
                        })
                      )
                      .then(close)
                      .then(() => {
                        setSetting({
                          alias: ESettingsAliases.TINKOFF_SBP_ENABLED,
                          value: '1',
                        });
                      })
                      .catch(() =>
                        notificationService.info({
                          t,
                          type: 'error',
                          title: t('errors.updated.title'),
                          dismiss: 3,
                        })
                      );
                  }}
                  isLoading={connectLoading}
                  disabled={!checkedBeneficiary}
                >
                  {t('save')}
                </Button>
              </div>
            )}
          </>
        )}
      </Preloader>
    </Modal>
  );
};
